import {
  UNIT_BATCH_MAX_ROWS,
  UNIT_CELL_FIELDS,
  type UnitBatchRequest,
  type UnitBatchResponse,
  type UnitCellField,
  unitBatchResponseSchema,
} from '@sivan/shared';
import { askConfirm } from '../ui/confirm';
import { useEffect, useMemo, useState } from 'react';
import type { SortColumn } from 'react-data-grid';
import { ApiError, NetworkError, apiPost } from '../api/client';
import { type ReviewPhase, ReviewDialog } from './ReviewDialog';
import { MobileUnitList } from './MobileUnitList';
import { UnitForm } from './UnitForm';
import { UnitGrid } from './UnitGrid';
import type { ClipboardTable } from './clipboard';
import {
  type Cells,
  type GridRow,
  type Structures,
  applyBatchResult,
  applyPaste,
  discardRow,
  keepMyChange,
  newRow,
  rowState,
  rowsFromUnits,
  setCell,
  summarize,
  toBatchRows,
} from './pending';
import type { ProjectData } from './use-master-data';
import { useNarrowScreen } from './use-narrow-screen';
import { reportPendingChanges, warnOnUnload } from './unsaved-changes';
import { and, count } from './labels';

interface UnitsWorkspaceProps {
  readonly projectId: string;
  readonly data: ProjectData;
  readonly canEditStored: boolean;
  readonly canCreate: boolean;
  /** Called after the server confirmed a save; reloads stored data (the message survives the reload). */
  readonly onSaved: (message: string) => void;
  /** Confirmation of the previous save, shown again after the reload. */
  readonly savedMessage: string | null;
}

interface Filters {
  readonly search: string;
  readonly building: string;
  readonly floor: string;
  /** Show only rows with pending changes (and the placeholder). */
  readonly pendingOnly: boolean;
}

function compare(a: string, b: string): number {
  const numeric = /^-?\d+(\.\d+)?$/;
  if (numeric.test(a) && numeric.test(b)) return Number(a) - Number(b);
  return a.localeCompare(b, 'he', { numeric: true });
}

/** Stored rows matching the filters, every pending row, then the placeholder; optionally sorted. */
function displayedRows(
  rows: readonly GridRow[],
  filters: Filters,
  sort: readonly SortColumn[],
): GridRow[] {
  const search = filters.search.trim().toLowerCase();
  const matches = (row: GridRow) =>
    rowState(row) !== 'stored' ||
    (!filters.pendingOnly &&
      (search === '' ||
        row.cells.code.toLowerCase().includes(search) ||
        row.cells.displayNumber.toLowerCase().includes(search)) &&
      (filters.building === '' || row.cells.building === filters.building) &&
      (filters.floor === '' || row.cells.floor === filters.floor));
  const body = rows.filter((row) => rowState(row) !== 'placeholder' && matches(row));
  const placeholder = rows.filter((row) => rowState(row) === 'placeholder');
  const [first] = sort;
  const field = (first?.columnKey ?? 'code') as UnitCellField;
  const direction = first?.direction === 'DESC' ? -1 : 1;
  // Natural order by default (A-2 before A-10); new rows stay after stored ones.
  const isNew = (row: GridRow) => (row.unitId === null ? 1 : 0);
  body.sort((a, b) =>
    first === undefined
      ? isNew(a) - isNew(b) || (a.unitId === null ? 0 : compare(a.cells.code, b.cells.code))
      : direction * compare(a.cells[field], b.cells[field]),
  );
  return [...body, ...placeholder];
}

/**
 * The units workspace of one project: the pending draft (memory only, DR-147), the grid or mobile
 * list, and the review → save flow. A save happens only after the server's preview and the user's
 * explicit confirmation; success is shown only after the server confirms the commit.
 */
export function UnitsWorkspace({
  projectId,
  data,
  canEditStored,
  canCreate,
  onSaved,
  savedMessage,
}: UnitsWorkspaceProps) {
  const structures: Structures = data.structures;
  const narrow = useNarrowScreen();
  const [rows, setRows] = useState<GridRow[]>(() => rowsFromUnits(data.units, structures));
  const [filters, setFilters] = useState<Filters>({
    search: '',
    building: '',
    floor: '',
    pendingOnly: false,
  });
  const [sort, setSort] = useState<readonly SortColumn[]>([]);
  const [message, setMessage] = useState<{
    text: string;
    tone: 'info' | 'success' | 'error';
  } | null>(savedMessage === null ? null : { tone: 'success', text: savedMessage });
  const [review, setReview] = useState<{
    batch: UnitBatchRequest;
    result: UnitBatchResponse | null;
    phase: ReviewPhase;
  } | null>(null);
  const [editing, setEditing] = useState<GridRow | null>(null);

  const displayed = useMemo(() => displayedRows(rows, filters, sort), [rows, filters, sort]);
  const pending = summarize(rows);
  const pendingTotal = pending.changed + pending.created;
  const ordered =
    sort.length > 0 ||
    filters.search !== '' ||
    filters.building !== '' ||
    filters.floor !== '' ||
    filters.pendingOnly;
  // Stored values that the pending changes would clear (explicitly emptied cells).
  const clearedValues = rows.reduce(
    (count, row) =>
      count +
      (row.base === null
        ? 0
        : UNIT_CELL_FIELDS.filter((f) => row.base?.[f] !== '' && row.cells[f].trim() === '')
            .length),
    0,
  );

  useEffect(() => {
    reportPendingChanges(pendingTotal);
  }, [pendingTotal]);
  useEffect(() => {
    window.addEventListener('beforeunload', warnOnUnload);
    return () => {
      window.removeEventListener('beforeunload', warnOnUnload);
      reportPendingChanges(0);
    };
  }, []);
  const hasPending = pending.changed + pending.created > 0;
  const floors = useMemo(
    () =>
      [
        ...new Set(
          structures
            .filter((s) => filters.building === '' || s.code === filters.building)
            .flatMap((s) => s.levels.map((l) => l.number)),
        ),
      ].sort((a, b) => a - b),
    [structures, filters.building],
  );

  const onPaste = (table: ClipboardTable, anchorIndex: number, anchorField: UnitCellField) => {
    const result = applyPaste(rows, displayed, table, anchorIndex, anchorField, structures);
    setRows(result.rows);
    const ignored =
      result.ignoredColumns > 0
        ? ` ${count(result.ignoredColumns, 'עמודה עודפת אחת לא הודבקה', 'עמודות עודפות לא הודבקו')}.`
        : '';
    setMessage({
      tone: result.ignoredColumns > 0 ? 'error' : 'info',
      text: `הודבקו ${count(table.length, 'שורה אחת', 'שורות')}: ${and(count(result.updatedRows, 'עדכון אחד', 'עדכונים'), count(result.newRows, 'שורה חדשה אחת', 'שורות חדשות'))}. דבר לא נשמר עדיין.${ignored}${ordered ? ' ההדבקה חלה על השורות כפי שהן מוצגות כעת (לפי הסינון והמיון).' : ''}`,
    });
  };

  const applyForm = (key: string, cells: Cells) => {
    let next = rows.some((row) => row.key === key)
      ? rows
      : [...rows.filter((r) => rowState(r) !== 'placeholder'), { ...newRow(), key }];
    for (const field of UNIT_CELL_FIELDS)
      next = setCell(next, key, field, cells[field], structures);
    setRows(next);
    setEditing(null);
  };

  const startReview = async () => {
    const batchRows = toBatchRows(rows);
    if (batchRows.length > UNIT_BATCH_MAX_ROWS) {
      setMessage({
        tone: 'error',
        text: `ניתן לשמור עד ${String(UNIT_BATCH_MAX_ROWS)} שורות בפעם אחת (ממתינות ${String(batchRows.length)}). שמרו בחלקים.`,
      });
      return;
    }
    const batch: UnitBatchRequest = { batchId: crypto.randomUUID(), rows: batchRows };
    setReview({ batch, result: null, phase: 'reviewing' });
    try {
      const result = await apiPost(
        `/projects/${projectId}/units/batch/preview`,
        batch,
        unitBatchResponseSchema,
      );
      setRows((current) => applyBatchResult(current, result));
      setReview({ batch, result, phase: 'reviewing' });
    } catch (error) {
      setReview(null);
      setMessage({ tone: 'error', text: failureText(error, 'בדיקת השינויים נכשלה.') });
    }
  };

  const commit = async () => {
    if (review === null) return;
    const { batch } = review;
    setReview({ ...review, phase: 'saving' });
    try {
      const result = await apiPost(
        `/projects/${projectId}/units/batch`,
        batch,
        unitBatchResponseSchema,
      );
      setReview(null);
      onSaved(
        `השינויים נשמרו: ${and(count(result.summary.create, 'יחידה חדשה אחת', 'יחידות חדשות'), count(result.summary.update, 'עדכון אחד', 'עדכונים'))}.`,
      );
    } catch (error) {
      if (error instanceof NetworkError) {
        // Outcome unknown: retrying with the same batch id cannot write twice.
        setReview({ ...review, phase: 'uncertain' });
        return;
      }
      const rejected =
        error instanceof ApiError ? unitBatchResponseSchema.safeParse(error.body['batch']) : null;
      if (rejected?.success === true) {
        setRows((current) => applyBatchResult(current, rejected.data));
        setReview({ batch, result: rejected.data, phase: 'reviewing' });
      } else {
        setReview(null);
        setMessage({ tone: 'error', text: failureText(error, 'השמירה נכשלה. דבר לא נשמר.') });
      }
    }
  };

  const discardAll = async () => {
    const question =
      pendingTotal === 1
        ? 'לבטל את השינוי הממתין?'
        : `לבטל את כל ${String(pendingTotal)} השינויים הממתינים?`;
    if (!(await askConfirm({ title: question, confirmLabel: 'ביטול השינויים', danger: true })))
      return;
    setRows(rowsFromUnits(data.units, structures));
    setMessage({ tone: 'info', text: 'השינויים הממתינים בוטלו.' });
  };

  return (
    <section className="workspace" aria-labelledby="units-title">
      <div className="toolbar">
        <h2 id="units-title" className="toolbar__title">
          יחידות
        </h2>
        <label className="toolbar__field">
          <span>חיפוש</span>
          <input
            type="search"
            value={filters.search}
            placeholder="קוד או מספר"
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
            }}
          />
        </label>
        <label className="toolbar__field">
          <span>בניין</span>
          <select
            value={filters.building}
            onChange={(e) => {
              setFilters({ ...filters, building: e.target.value, floor: '' });
            }}
          >
            <option value="">הכול</option>
            {structures.map((s) => (
              <option key={s.id} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar__field">
          <span>קומה</span>
          <select
            value={filters.floor}
            onChange={(e) => {
              setFilters({ ...filters, floor: e.target.value });
            }}
          >
            <option value="">הכול</option>
            {floors.map((floor) => (
              <option key={floor} value={String(floor)}>
                {floor}
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar__toggle">
          <input
            type="checkbox"
            checked={filters.pendingOnly}
            onChange={(e) => {
              setFilters({ ...filters, pendingOnly: e.target.checked });
            }}
          />
          <span>שינויים ממתינים בלבד</span>
        </label>
        <div className="toolbar__actions">
          <p className="pending-count" aria-live="polite">
            {hasPending
              ? `ממתינים לשמירה: ${count(pending.changed, 'עדכון אחד', 'עדכונים')}, ${count(pending.created, 'יחידה חדשה אחת', 'חדשות')}${pending.withErrors > 0 ? `, ${count(pending.withErrors, 'אחת עם שגיאה', 'עם שגיאות')}` : ''}`
              : 'אין שינויים ממתינים'}
          </p>
          {narrow && canCreate && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setEditing(newRow());
              }}
            >
              הוספת יחידה
            </button>
          )}
          <button
            type="button"
            className="button"
            disabled={!hasPending}
            onClick={() => void discardAll()}
          >
            ביטול שינויים
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!hasPending}
            onClick={() => void startReview()}
          >
            בדיקה ושמירה
          </button>
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className={message === null ? 'visually-hidden' : `notice notice--${message.tone}`}
      >
        {message?.text}
      </div>
      {data.truncated && (
        <p className="notice notice--warning">מוצגות {data.units.length} היחידות הראשונות בלבד.</p>
      )}

      {narrow ? (
        <MobileUnitList rows={displayed} canEditStored={canEditStored} onEdit={setEditing} />
      ) : (
        <>
          <details className="paste-help">
            <summary>הדבקה מ-Excel: איך זה עובד ומה לא נתמך</summary>
            <p>
              ניתן להעתיק טווח מ-Excel ולהדביק אותו (Ctrl/Cmd+V) החל מהתא הנבחר. הדבקה בשורה "שורה
              חדשה" שבסוף הטבלה יוצרת יחידות חדשות; שורות נוספות נוצרות אוטומטית. תא ריק בהדבקה אינו
              משנה ערך קיים; למחיקה ערכו את התא ורוקנו אותו. דבר לא נשמר עד לבדיקה ולאישור.
            </p>
            <p>
              לא נתמכים כרגע: ביטול פעולה (Ctrl/Cmd+Z), בחירת טווח והעתקה מתוך הטבלה, גרירת מילוי,
              מחיקת יחידות. נוסחאות נשמרות כטקסט ואינן מחושבות.
            </p>
          </details>
          <UnitGrid
            rows={displayed}
            canEditStored={canEditStored}
            canCreate={canCreate}
            sortColumns={sort}
            onSortColumnsChange={setSort}
            onCellChange={(key, field, value) => {
              setRows((current) => setCell(current, key, field, value, structures));
            }}
            onPaste={onPaste}
            onPasteRejected={(text) => {
              setMessage({ tone: 'error', text });
            }}
          />
        </>
      )}

      {editing !== null && (
        <UnitForm
          key={editing.key}
          row={editing}
          onApply={applyForm}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      <ReviewDialog
        open={review !== null}
        phase={review?.phase ?? 'reviewing'}
        result={review?.result ?? null}
        rows={rows}
        clearedValues={clearedValues}
        onConfirm={() => void commit()}
        onClose={() => {
          setReview(null);
        }}
        onKeepMine={(key) => {
          setRows((current) => keepMyChange(current, key));
          setReview(null);
        }}
        onDiscard={(key) => {
          const conflict = rows.find((row) => row.key === key)?.outcome === 'conflict';
          setRows((current) => discardRow(current, key));
          setReview(null);
          if (conflict) {
            setMessage({
              tone: 'info',
              text: 'השינוי בוטל. ייתכן שהשורה מציגה ערכים ישנים; הם יתעדכנו לאחר השמירה הבאה או טעינה מחדש.',
            });
          }
        }}
      />
    </section>
  );
}

function failureText(error: unknown, fallback: string): string {
  if (error instanceof NetworkError) return `${fallback} אין חיבור לשרת.`;
  if (error instanceof ApiError && error.status === 409)
    return `${fallback} הנתונים השתנו במקביל על ידי משתמש אחר. לחצו שוב על "בדיקה ושמירה".`;
  if (error instanceof ApiError && (error.status === 403 || error.status === 404))
    return 'אין לך הרשאה לבצע פעולה זו.';
  if (error instanceof ApiError && error.requestId !== null)
    return `${fallback} מזהה בקשה: ${error.requestId}`;
  return fallback;
}
